import { useState } from 'react';
import { useI18n } from './i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const tagColors = [
  'bg-[#34B2C8]/10 text-[#34B2C8]',
  'bg-[#F5A623]/10 text-[#F5A623]',
  'bg-emerald-50 text-emerald-600',
  'bg-rose-50 text-rose-600',
  'bg-violet-50 text-violet-600',
];

const featureImages = [
  'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/pbnhx64h_Messagerie.png',
  'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/toy7pwgm_image.png',
  'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/ao4bxi5a_Graph.png',
  'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/yud12r9d_Exploitation%20de%20documents%20strat%C3%A9giques.png',
  'https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/63tnpe9q_Augment%C3%A9e%20par%20l%27IA.png',
];

const BrowserFrame = ({ src, alt, onClick }) => (
  <div
    onClick={onClick}
    className="rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 overflow-hidden transition-transform duration-300 hover:scale-[1.02] cursor-pointer"
    role="button"
    tabIndex={0}
    data-testid={`feature-image-${alt}`}
  >
    <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-300/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-300/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-300/60" />
      </div>
      <div className="flex-1 mx-6">
        <div className="h-5 bg-slate-100 rounded-md w-48 mx-auto flex items-center justify-center">
          <span className="text-[9px] text-slate-400 tracking-wide">anticiper.app</span>
        </div>
      </div>
    </div>
    <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
  </div>
);

const Lightbox = ({ src, alt, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 cursor-pointer"
      data-testid="lightbox-overlay"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
        data-testid="lightbox-close"
      >
        <X size={20} className="text-white" />
      </button>
      <motion.img
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        src={src}
        alt={alt}
        onClick={e => e.stopPropagation()}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl cursor-default"
      />
    </motion.div>
  </AnimatePresence>
);

export default function LandingDemo() {
  const { t } = useI18n();
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.span
              variants={fadeUp}
              custom={0}
              className="inline-block text-xs font-semibold tracking-[0.2em] text-[#34B2C8] mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {t.demo.overline}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t.demo.title}
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base text-slate-500 leading-relaxed">
              {t.demo.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="space-y-32">
            {t.demo.features.map((feature, i) => {
              const isReversed = i % 2 === 1;
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-100px' }}
                  className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center"
                >
                  <div className={isReversed ? 'md:order-2' : ''}>
                    <motion.span
                      variants={fadeUp}
                      custom={0}
                      className={`inline-block text-[10px] font-semibold tracking-[0.15em] px-3 py-1 rounded-full mb-6 ${tagColors[i]}`}
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {feature.tag}
                    </motion.span>
                    <motion.h3
                      variants={fadeUp}
                      custom={1}
                      className="text-2xl sm:text-3xl font-medium text-slate-900 mb-4"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {feature.title}
                    </motion.h3>
                    <motion.p variants={fadeUp} custom={2} className="text-sm text-slate-500 leading-relaxed">
                      {feature.desc}
                    </motion.p>
                  </div>
                  <motion.div variants={fadeUp} custom={2} className={isReversed ? 'md:order-1' : ''}>
                    <BrowserFrame
                      src={featureImages[i]}
                      alt={feature.title}
                      onClick={() => setLightbox({ src: featureImages[i], alt: feature.title })}
                    />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </>
  );
}
